/**
 * Collection Load More Pagination
 * Handles infinite scroll / load more functionality for collection products
 * Products are appended to existing grid instead of replacing content
 * Preserves tab-collection parameter during pagination
 */

class CollectionLoadMore {
  constructor() {
    this.pagination = document.querySelector('[data-pagination-load-more]');
    if (!this.pagination) {
      return;
    }

    this.button = this.pagination.querySelector('button');
    if (!this.button) {
      return;
    }

    this.buttonText = this.button.querySelector('.button__text');
    this.buttonLoading = this.button.querySelector('.button__loading');
    this.productList = document.querySelector('product-list');
    this.collectionContainer = document.querySelector('[data-collection-container]');
    this.isLoading = false;
    this.abortController = null;

    this.init();
  }

  init() {
    if (!this.button) return;

    // Bind the method to ensure correct 'this' context
    this.loadMore = this.loadMore.bind(this);
    
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.loadMore();
    });

    // Listen for tab changes to reset pagination
    window.addEventListener('collection:changed', () => {
      this.resetPagination();
    });

    // Listen for section reloads (after tab switch)
    document.addEventListener('section:loaded', () => {
      this.resetPagination();
    });
  }

  async loadMore() {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoadingState();

    try {
      const nextPageUrl = this.button.dataset.nextPageUrl;
      const sectionId =
        this.button.dataset.sectionId ||
        document.querySelector('.shopify-section--main-collection')?.id?.replace('shopify-section-', '');

      const urlParams = window.CollectionUrlParams;
      const fetchUrl = urlParams
        ? urlParams.buildLoadMoreFetchUrl(nextPageUrl, sectionId)
        : nextPageUrl;

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      const response = await fetch(fetchUrl, {
        cache: 'no-store',
        signal: this.abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const parser = new DOMParser();
      let doc;

      if (sectionId) {
        const data = await response.json();
        const sectionHtml = data[sectionId];

        if (!sectionHtml) {
          throw new Error('Section not found in response');
        }

        doc = parser.parseFromString(sectionHtml, 'text/html');
      } else {
        const html = await response.text();
        doc = parser.parseFromString(html, 'text/html');
      }

      const newProducts = doc.querySelectorAll('product-list > product-card');
      const existingHandles = new Set(
        [...this.productList.querySelectorAll('product-card[handle]')].map(
          (card) => card.getAttribute('handle')
        )
      );

      let appendedCount = 0;

      newProducts.forEach((product) => {
        const handle = product.getAttribute('handle');

        if (handle && existingHandles.has(handle)) {
          return;
        }

        if (handle) {
          existingHandles.add(handle);
        }

        this.productList.appendChild(product.cloneNode(true));
        appendedCount += 1;
      });

      const newPagination = doc.querySelector('[data-pagination-load-more]');

      if (appendedCount > 0) {
        document.dispatchEvent(
          new CustomEvent('collection:productsLoaded', {
            detail: { count: appendedCount }
          })
        );
      }

      if (newPagination) {
        const newButton = newPagination.querySelector('button');
        this.button.dataset.nextPageUrl = newButton.dataset.nextPageUrl;
        this.button.dataset.currentPage = newButton.dataset.currentPage;

        const newInfo = newPagination.querySelector('.pagination__info');
        const currentInfo = this.pagination.querySelector('.pagination__info');

        if (newInfo && currentInfo) {
          currentInfo.textContent = newInfo.textContent;
        }
      } else if (appendedCount > 0 || newProducts.length > 0) {
        this.pagination.style.display = 'none';
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      
      this.showError();
    } finally {
      this.isLoading = false;
      this.hideLoadingState();
    }
  }

  showLoadingState() {
    this.button.disabled = true;
    this.buttonText.style.display = 'none';
    this.buttonLoading.style.display = 'inline-block';
  }

  hideLoadingState() {
    this.button.disabled = false;
    this.buttonText.style.display = 'inline-block';
    this.buttonLoading.style.display = 'none';
  }

  showError() {
    const originalText = this.buttonText.textContent;
    this.buttonText.textContent = 'Error loading products. Click to retry.';
    
    setTimeout(() => {
      this.buttonText.textContent = originalText;
    }, 3000);
  }

  resetPagination() {
    // When collection tab changes, we need to wait for the new content
    // to be loaded and then reinitialize
    setTimeout(() => {
      const newPagination = document.querySelector('[data-pagination-load-more]');
      if (newPagination && newPagination !== this.pagination) {
        this.pagination = newPagination;
        this.button = this.pagination.querySelector('button');
        this.buttonText = this.button.querySelector('.button__text');
        this.buttonLoading = this.button.querySelector('.button__loading');
        this.productList = document.querySelector('product-list');
        this.init();
      }
    }, 100);
  }
}

// Global instance
let collectionLoadMoreInstance = null;

// Initialize function
function initCollectionLoadMore() {
  // Destroy previous instance if exists
  if (collectionLoadMoreInstance) {
    collectionLoadMoreInstance = null;
  }
  
  // Create new instance
  const instance = new CollectionLoadMore();
  if (instance.button) {
    collectionLoadMoreInstance = instance;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionLoadMore);
} else {
  // DOM is already ready
  initCollectionLoadMore();
}

// Reinitialize after section updates (for Shopify's Section Rendering API)
document.addEventListener('shopify:section:load', () => {
  setTimeout(initCollectionLoadMore, 100);
});

// Also reinitialize when section is reloaded
document.addEventListener('section:loaded', () => {
  setTimeout(initCollectionLoadMore, 100);
});
